/**
 * Voyage Panel
 *
 * What day it is, who's aboard, what's left to eat — and, when nothing else is
 * driving, the DM's "Advance Day" button.
 *
 * WHO ADVANCES THE DAY. The OBR extension is the primary interface: on a hex map
 * the DM can see, dragging the ship to where it really went says more than a d20
 * can, so that drag resolves the day (feeds the crew, runs the navigation state
 * machine, moves the believed token down the plotted line). This panel is the
 * fallback for sessions running without the map.
 *
 * Both paths write the same fields, so if both were live the same day would be
 * counted twice and the crew would eat twice. We detect the map by
 * `navigation.updatedBy === 'extension'` — the field exists for exactly this —
 * and stand the roll-driven controls down behind a deliberate click when it is
 * set. It is advisory, not a lock: last writer wins, and it self-corrects, since
 * resolving here stamps `updatedBy: 'dashboard'` and the next drag stamps it back.
 */

import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../@/components/ui/card';
import { Button } from '../@/components/ui/button';
import { Input } from '../@/components/ui/input';
import { Label } from '../@/components/ui/label';
import { Badge } from '../@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../@/components/ui/select';
// Aliased: the bare `Map` export would shadow the global Map constructor.
import { Map as MapIcon, Minus, Plus, Sun, Utensils, Users } from 'lucide-react';
import { useSession } from '../session-context';
import { defaultNavigationState } from '../types/navigation-types';
import { resolveDay, hexDistance } from '../utils/nav-utils';
import { NAVIGATION_DC_OPTIONS, DEFAULT_NAVIGATION_DC } from '../utils/navigation-dcs';
import { calculateCourseSettingDC } from '../utils/course-setting';

export const VoyagePanel: React.FC = () => {
  const {
    gameState, isDM, updateGameState, advanceDay, endVoyage,
    updateNavigation, addNavLogEntries
  } = useSession();

  const userIsDM = isDM();
  const [navDC, setNavDC] = useState<number>(DEFAULT_NAVIGATION_DC);
  const [navRoll, setNavRoll] = useState<string>('');
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);
  // Set when the DM deliberately overrides the map-is-driving notice.
  const [showFallback, setShowFallback] = useState(false);

  // The map wrote the navigation state last, so the DM is resolving days by
  // dragging tokens in Owlbear rather than typing rolls here. See the header.
  // Read before the early return below — hooks depend on it.
  const mapDriven = gameState?.navigation?.updatedBy === 'extension';
  const navRev = gameState?.navigation?.rev ?? 0;

  // If the map writes again after the DM overrode the notice, put it back —
  // otherwise the override sticks for the rest of the session and the roll form
  // quietly stays up while Owlbear is also resolving days.
  useEffect(() => {
    if (mapDriven) setShowFallback(false);
  }, [navRev, mapDriven]);

  if (!gameState) return null;

  const crewSize = gameState.crewSize ?? 0;
  const provisions = gameState.provisions ?? 0;
  const voyageDay = gameState.voyageDay ?? 0;
  const navigation = gameState.navigation ?? defaultNavigationState;
  const destination = gameState.voyageDestination;

  const daysRemaining = crewSize > 0 ? Math.floor(provisions / crewSize) : Infinity;
  const starving = crewSize > 0 && provisions < crewSize;

  const navActive = navigation.status !== 'not-charted' && navigation.status !== 'arrived';
  // How far the crew has sailed the believed token since the last resolved day.
  // That distance is the day's run, and therefore how far the ship drifts if it
  // turns out to be off course.
  const dayRun = navigation.dayStartBelieved
    ? hexDistance(navigation.dayStartBelieved, navigation.believed.hex)
    : 0;

  // Re-charting uses the original course-setting DC, but the crew no longer
  // knows where they started from.
  const rechartDC = destination
    ? calculateCourseSettingDC({
        distance: destination.distance,
        familiarity: destination.familiarity,
        startPointKnowledge: 'completely unknown',
        chartQuality: destination.chartQuality
      })
    : navDC;

  const adjust = async (key: 'crewSize' | 'provisions', delta: number) => {
    const current = key === 'crewSize' ? crewSize : provisions;
    await updateGameState(key, Math.max(0, current + delta));
  };

  const handleAdvanceDay = async () => {
    const roll = parseInt(navRoll, 10);
    setIsAdvancing(true);
    try {
      const { day, shortfall } = await advanceDay();

      const notes: string[] = [`Day ${day}.`];
      if (shortfall > 0) {
        notes.push(`Provisions ran out — ${shortfall} of the crew went hungry today.`);
      }

      // A roll is optional: the DM can advance a day of calm coastal sailing
      // without putting the navigation state machine through its paces.
      if (!isNaN(roll) && navigation.status !== 'not-charted' && navigation.status !== 'arrived') {
        const result = resolveDay({
          nav: navigation,
          roll,
          onCourseDC: navDC,
          rechartDC,
          navConsecutiveFailures: gameState.consecutiveFailures?.navigation ?? 0,
          day,
          pilotName: gameState.navigatorName || 'The navigator'
        });

        await updateNavigation(result.nav);
        await addNavLogEntries(result.logs);
        await updateGameState('consecutiveFailures', {
          navigation: result.navConsecutiveFailures
        });
        notes.push(result.summary);
      }

      setSummary(notes.join(' '));
      setNavRoll('');
    } finally {
      setIsAdvancing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Sun className="h-4 w-4" /> Voyage Day
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-bold">{voyageDay}</p>
            {destination && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Bound for {destination.name}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="h-4 w-4" /> Crew
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-bold">{crewSize}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              eats {crewSize} provisions per day
            </p>
            {userIsDM && (
              <div className="flex gap-2 mt-3">
                <Button variant="outline" size="sm" onClick={() => adjust('crewSize', -1)}>
                  <Minus className="h-3 w-3" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => adjust('crewSize', 1)}>
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className={starving ? 'border-red-400 dark:border-red-700' : undefined}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Utensils className="h-4 w-4" /> Provisions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-4xl font-bold">{provisions}</p>
            {starving ? (
              <Badge variant="destructive" className="mt-1">Not enough for one more day</Badge>
            ) : (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {daysRemaining === Infinity ? 'No crew aboard' : `${daysRemaining} days remaining`}
              </p>
            )}
            {userIsDM && (
              <div className="flex gap-2 mt-3">
                <Button variant="outline" size="sm" onClick={() => adjust('provisions', -1)}>
                  <Minus className="h-3 w-3" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => adjust('provisions', 1)}>
                  <Plus className="h-3 w-3" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => adjust('provisions', crewSize)}>
                  +1 day
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {userIsDM ? (
        <Card>
          <CardHeader>
            <CardTitle>Advance the Voyage</CardTitle>
            <CardDescription>
              {mapDriven && !showFallback
                ? 'The Owlbear map is driving this voyage.'
                : 'One day passes: the crew eats, and the navigator takes a bearing. Leave the roll blank to pass a day without a navigation check.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {mapDriven && !showFallback ? (
              <div className="space-y-3">
                <div className="flex gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded text-sm">
                  <MapIcon className="h-4 w-4 mt-0.5 shrink-0 text-blue-700 dark:text-blue-300" />
                  <div className="space-y-1">
                    <p>
                      Sail the blue ship token to where the crew went, then press
                      Advance Day in the extension. Dragging on its own changes
                      nothing — the button feeds the crew, takes the navigation
                      check, and drifts the red truth token if the ship is off
                      course.
                    </p>
                    <p className="text-gray-600 dark:text-gray-400">
                      Sailed since the last resolved day:{' '}
                      <span className="font-medium">{dayRun} hex{dayRun === 1 ? '' : 'es'}</span>
                      {navActive && ` — the ship drifts ${Math.max(1, dayRun)} if it has lost its course.`}
                    </p>
                  </div>
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  className="text-gray-600 dark:text-gray-400"
                  onClick={() => setShowFallback(true)}
                >
                  Advance from here instead
                </Button>
              </div>
            ) : (
              <>
                {mapDriven && (
                  <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-800 rounded text-sm text-amber-800 dark:text-amber-200">
                    The map resolved the last day. Advancing here as well would
                    count the same day twice and feed the crew twice — only do this
                    if Owlbear is closed.
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Conditions (navigation DC)</Label>
                    <Select value={String(navDC)} onValueChange={(v) => setNavDC(Number(v))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {NAVIGATION_DC_OPTIONS.map((o) => (
                          <SelectItem key={o.label} value={String(o.value)}>
                            {o.label} (DC {o.value})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="nav-roll">
                      {gameState.navigatorName || 'Navigator'} — Knowledge (geography) total
                    </Label>
                    <Input
                      id="nav-roll"
                      type="number"
                      value={navRoll}
                      onChange={(e) => setNavRoll(e.target.value)}
                      placeholder="d20 + skill bonus"
                    />
                  </div>
                </div>

                <Button onClick={handleAdvanceDay} disabled={isAdvancing}>
                  {isAdvancing ? 'Advancing...' : 'Advance Day'}
                </Button>
              </>
            )}

            <div>
              <Button variant="outline" onClick={endVoyage} disabled={isAdvancing}>
                End Voyage
              </Button>
            </div>

            {summary && (
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded text-sm">
                {summary}
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-8 text-center text-gray-600 dark:text-gray-400">
            <p>The DM advances the voyage day by day.</p>
            <p className="text-sm mt-2">
              Watch the provisions — every soul aboard eats one point a day.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default VoyagePanel;
